import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { CourseCard } from '../components/CourseCard';
import { useCourses, categories, types } from '../hooks/useCourses';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Filter, X, Loader2, Video, Users } from 'lucide-react';

// Course delivery types
const deliveryTypes = [
  { id: 'all', name: { ja: 'すべて', en: 'All', pt: 'Todos', id: 'Semua', vi: 'Tất cả', es: 'Todos', tl: 'Lahat' } },
  { id: 'in-person', name: { ja: '対面講習', en: 'In-Person', pt: 'Presencial', id: 'Tatap Muka', vi: 'Trực tiếp', es: 'Presencial', tl: 'Personal' } },
  { id: 'on-demand', name: { ja: 'オンデマンド', en: 'On-Demand', pt: 'Sob Demanda', id: 'Sesuai Permintaan', vi: 'Theo yêu cầu', es: 'Bajo Demanda', tl: 'On-Demand' } },
  { id: 'hybrid', name: { ja: 'ハイブリッド', en: 'Hybrid', pt: 'Híbrido', id: 'Hibrida', vi: 'Kết hợp', es: 'Híbrido', tl: 'Hybrid' } },
];

export default function Courses() {
  const { t, language } = useLanguage();
  const { courses, loading, error, fetchCourses } = useCourses();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedType = searchParams.get('type') || 'all';
  const selectedDelivery = searchParams.get('delivery') || 'all';

  const filteredCourses = useMemo(() => {
    return courses.filter(course => {
      const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
      const matchesType = selectedType === 'all' || course.type === selectedType;
      const matchesDelivery = selectedDelivery === 'all' || 
        (course.courseType || 'in-person') === selectedDelivery;
      const matchesSearch = searchTerm === '' || 
        (course.name[language] || course.name.ja).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (course.description[language] || course.description.ja).toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesType && matchesDelivery && matchesSearch;
    });
  }, [courses, selectedCategory, selectedType, selectedDelivery, searchTerm, language]);

  const handleCategoryChange = (categoryId) => {
    setSearchParams(prev => {
      if (categoryId === 'all') {
        prev.delete('category');
      } else {
        prev.set('category', categoryId);
      }
      return prev;
    });
  };

  const handleTypeChange = (typeId) => {
    setSearchParams(prev => {
      if (typeId === 'all') {
        prev.delete('type');
      } else {
        prev.set('type', typeId);
      }
      return prev;
    });
  };

  const handleDeliveryChange = (deliveryId) => {
    setSearchParams(prev => {
      if (deliveryId === 'all') {
        prev.delete('delivery');
      } else {
        prev.set('delivery', deliveryId);
      }
      return prev;
    });
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchTerm('');
  };

  const hasActiveFilters = selectedCategory !== 'all' || selectedType !== 'all' || selectedDelivery !== 'all' || searchTerm !== '';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="courses-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('courses.title')}
          </h1>
          <p className="text-gray-600 max-w-2xl">
            {t('coursesPage.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-8">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder={t('coursesPage.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
              data-testid="course-search"
            />
          </div>

          {/* Category Filter */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                {t('coursesPage.category')}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCategoryChange(category.id)}
                  className={selectedCategory === category.id 
                    ? 'bg-[#00A651] hover:bg-[#008a43] text-white' 
                    : 'border-gray-300 hover:border-[#00A651] hover:text-[#00A651]'
                  }
                  data-testid={`filter-category-${category.id}`}
                >
                  {category.name[language] || category.name.ja}
                </Button>
              ))}
            </div>
          </div>

          {/* Type Filter */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">
                {t('coursesPage.trainingType')}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {types.map((type) => (
                <Button
                  key={type.id}
                  variant={selectedType === type.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTypeChange(type.id)}
                  className={selectedType === type.id 
                    ? type.id === 'skill' 
                      ? 'bg-[#00A651] hover:bg-[#008a43] text-white'
                      : type.id === 'special'
                        ? 'bg-[#FACC15] hover:bg-[#eab308] text-gray-900'
                        : 'bg-[#00A651] hover:bg-[#008a43] text-white'
                    : 'border-gray-300 hover:border-[#00A651] hover:text-[#00A651]'
                  }
                  data-testid={`filter-type-${type.id}`}
                >
                  {type.name[language] || type.name.ja}
                </Button>
              ))}
            </div>
          </div>

          {/* Delivery Type Filter */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Video className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                {t('coursesPage.deliveryType')}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {deliveryTypes.map((delivery) => (
                <Button
                  key={delivery.id}
                  variant={selectedDelivery === delivery.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleDeliveryChange(delivery.id)}
                  className={selectedDelivery === delivery.id 
                    ? delivery.id === 'on-demand' || delivery.id === 'hybrid'
                      ? 'bg-orange-500 hover:bg-orange-600 text-white'
                      : 'bg-[#00A651] hover:bg-[#008a43] text-white'
                    : 'border-gray-300 hover:border-[#00A651] hover:text-[#00A651]'
                  }
                  data-testid={`filter-delivery-${delivery.id}`}
                >
                  {delivery.id === 'on-demand' || delivery.id === 'hybrid' ? (
                    <Video className="h-3 w-3 mr-1" />
                  ) : delivery.id === 'in-person' ? (
                    <Users className="h-3 w-3 mr-1" />
                  ) : null}
                  {delivery.name[language] || delivery.name.ja}
                </Button>
              ))}
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-gray-500 hover:text-gray-700"
              data-testid="clear-filters"
            >
              <X className="h-4 w-4 mr-1" />
              {t('coursesPage.clearFilters')}
            </Button>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            {filteredCourses.length} {t('coursesPage.coursesFound')}
          </p>
        </div>

        {/* Course Grid */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#00A651]" />
            <p className="text-gray-500 mt-4">
              {t('common.loading')}
            </p>
          </div>
        ) : filteredCourses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <p className="text-gray-500 text-lg">
              {t('coursesPage.noCoursesFound')}
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={clearFilters}
            >
              {t('coursesPage.clearFilters')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
